import { createPinia } from 'pinia';
import { createPersistedState, type StorageLike } from 'pinia-plugin-persistedstate';
import SecureLS from 'secure-ls';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { createGuardedStorage, hydratePersistedStore } from '@/modules/core/utils/guarded-persist-storage.util.ts';
import { persistStoreIdFromKey, persistStoreKey } from '@/modules/core/utils/persisted-store-key.util.ts';

const pinia = createPinia();

const ls = new SecureLS({
  encodingType: 'aes',
  encryptionSecret: '3jh38353hiiw8o4hh333',
  isCompression: false,
});

function readSecureItem(key: string): string | null {
  const value = ls.get(key);

  return typeof value === 'string' && value !== '' ? value : null;
}

const secureLocalStorage: StorageLike = {
  getItem: readSecureItem,
  setItem: (key, value) => {
    if (readSecureItem(key) === value) {
      return;
    }

    ls.set(key, value);
  },
};

const persistStorage = createGuardedStorage(import.meta.env.DEV ? localStorage : secureLocalStorage);

pinia.use(
  createPersistedState({
    key: (id) => persistStoreKey(id),
    storage: persistStorage,
  })
);

const stores = new Map();
pinia.use(({ store }) => void stores.set(store.$id, store));

const SYNC_STORE_IDS: string[] = [
  StoreId.Access,
  StoreId.Auth,
  StoreId.Autocorrect,
  StoreId.Roles,
  StoreId.SyncSystem,
  StoreId.User,
  StoreId.FileEditor,
];

window.addEventListener('storage', ({ key }) => {
  if (!key) {
    return;
  }

  const id = persistStoreIdFromKey(key);

  if (!id || !SYNC_STORE_IDS.includes(id)) {
    return;
  }

  const store = stores.get(id);

  if (store) {
    hydratePersistedStore(store, key);
  }
});

export default pinia;
